/**
 * Third-party AI consent (U-7, D-7). After sign-in the app asks `GET /user/ai-consent`; while the current
 * version is not accepted it shows the consent sheet, and a decline blocks the app behind an
 * "AI consent needed" screen (Review and accept / Sign out). Consent lives on the server, per user, with a
 * version, so a policy change asks again.
 *
 * Enforced only in companion mode for now. Turning it on for the web later is one flag:
 * `AI_CONSENT_ENFORCED_ON_WEB`.
 */

export const AI_CONSENT_ENFORCED_ON_WEB = false

export interface AiConsentStatus {
    current_version: string
    accepted_version: string | null
    accepted_at: string | null
    declined_at: string | null
    required: boolean
    subprocessors: string[]
}

/** Named in the sheet when the server sends none (decision D-7). */
export const DEFAULT_AI_SUBPROCESSORS = ['OpenAI', 'Anthropic', 'TypeSafe AI']

/**
 * - `idle`: not enforced here (web, staff impersonation, signed out) — render nothing.
 * - `loading`: enforced, status not known yet — render nothing (the app paints underneath).
 * - `ok`: accepted.
 * - `sheet`: show the consent sheet (first ask, or "Review and accept" from the blocked screen).
 * - `blocked`: declined — the blocking screen.
 */
export type AiConsentGateState = 'idle' | 'loading' | 'ok' | 'sheet' | 'blocked'

export interface AiConsentGateInput {
    enforced: boolean
    signedIn: boolean
    impersonating?: boolean
    status: AiConsentStatus | null
    /** The status request failed (an older backend or offline): do not lock anyone out. */
    unavailable?: boolean
    /** "Review and accept" tapped on the blocked screen. */
    reviewing?: boolean
}

export function aiConsentGateState(input: AiConsentGateInput): AiConsentGateState {
    if (!input.enforced || !input.signedIn || input.impersonating) return 'idle'
    if (input.unavailable) return 'ok'
    if (!input.status) return 'loading'
    if (!input.status.required) return 'ok'
    if (input.reviewing) return 'sheet'
    return input.status.declined_at ? 'blocked' : 'sheet'
}

export function parseAiConsentStatus(data: unknown): AiConsentStatus | null {
    if (!data || typeof data !== 'object') return null
    const d = data as Record<string, unknown>
    if (typeof d.current_version !== 'string' || typeof d.required !== 'boolean') return null
    const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
    const subs = Array.isArray(d.subprocessors) ? d.subprocessors.filter((s): s is string => typeof s === 'string' && !!s) : []
    return {
        current_version: d.current_version,
        accepted_version: str(d.accepted_version),
        accepted_at: str(d.accepted_at),
        declined_at: str(d.declined_at),
        required: d.required,
        subprocessors: subs.length ? subs : DEFAULT_AI_SUBPROCESSORS,
    }
}

/** "OpenAI, Anthropic and TypeSafe AI". */
export function joinNames(names: string[]): string {
    if (names.length <= 1) return names[0] ?? ''
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}
